import { DefaultTheme } from 'styled-components';
import { TableProps } from 'react-virtualized';
import { StateIndicator } from './customer-state-indicator';
import { Customer, State, Recipient, Share, StateFamily, UserJourneyState, UserSegment } from '@algoan/dashboard/model';

export const cellSpacing = 1;
export const headerMargin = 15;
export const sectionSpacing = 15;
export const rowHeight = 32 + cellSpacing; // Adding "cellSpacing" is needed because of box-sizing value applied to row
export const invitationColumnWidth = 40;
export const spacerWidth = 8;

interface PartnerName {
  name: string;
}

export interface ExtendedTableProps extends TableProps {
  hasRows: boolean;
}

export const customerHasAnalysis = (customer: Customer): boolean => customer.dashboard.numberOfAnalyses > 0;

export const getColorFromDashboardState = (
  state: State,
  isAnonymousCustomer: boolean,
  theme: DefaultTheme,
): { color?: string; background?: string; disabled?: boolean } | undefined => {
  if (isAnonymousCustomer) {
    return { color: 'rgba(127, 162, 181, 0.6)', background: 'none', disabled: true };
  }
  switch (state) {
    case 'NEW':
      return { color: theme.algoanBlue };
    case 'VIEWED':
      return undefined; // default color is used
    case 'ARCHIVED':
      return { color: 'rgba(127, 162, 181, 0.6)', background: 'none' };
  }
};

export const convertUserJourneyStateToStateFamily = (
  userJourneyState: UserJourneyState,
  userSegment?: string,
): StateFamily => {
  if (userSegment === UserSegment.PDF_POC) {
    switch (userJourneyState) {
      case UserJourneyState.ANALYSIS_SUCCESS:
        return StateFamily.ANALYSIS_SUCCESS_PDF;
      case UserJourneyState.ANALYSIS_ERROR:
        return StateFamily.IN_ERROR_PDF;
      default:
        return StateFamily.PENDING_PDF;
    }
  }
  switch (userJourneyState) {
    case UserJourneyState.CUSTOMER_CREATED:
      return StateFamily.VOID;
    case UserJourneyState.STARTED:
    case UserJourneyState.QR_CODE_SCANNED:
    case UserJourneyState.BANK_SELECTED:
      return StateFamily.FLOW_STARTED;
    case UserJourneyState.AGGREGATION_STARTED:
    case UserJourneyState.AGGREGATION_FINISHED_SUCCESS:
    case UserJourneyState.FINISHED_SUCCESS:
      return StateFamily.AGGREGATION_STARTED;
    case UserJourneyState.INVITATION_SENT:
      return StateFamily.PENDING;
    case UserJourneyState.ANALYSIS_ERROR:
    case UserJourneyState.AGGREGATION_FINISHED_ERROR:
    case UserJourneyState.FINISHED_ERROR:
      return StateFamily.IN_ERROR;
    case UserJourneyState.ANALYSIS_SUCCESS:
      return StateFamily.HAS_ANALYSIS;
    case UserJourneyState.CONSENT_GIVEN:
      return StateFamily.FLOW_STARTED;
    default:
      return StateFamily.VOID;
  }
};

export const convertStateFamilyToIndicatorColor = (stateFamily: StateFamily): StateIndicator => {
  switch (stateFamily) {
    case StateFamily.HAS_ANALYSIS:
      return StateIndicator.GREEN;
    case StateFamily.AGGREGATION_STARTED:
      return StateIndicator.ORANGE;
    case StateFamily.IN_ERROR:
      return StateIndicator.RED;
    case StateFamily.VOID:
      return StateIndicator.VOID;
    case StateFamily.PENDING:
      return StateIndicator.PENDING;
    case StateFamily.FLOW_STARTED:
      return StateIndicator.GREY;
    case StateFamily.ANALYSIS_SUCCESS_PDF:
      return StateIndicator.GREEN;
    case StateFamily.PENDING_PDF:
      return StateIndicator.ORANGE;
    case StateFamily.IN_ERROR_PDF:
      return StateIndicator.RED;
  }
};

export const mapLogosNames = (shares: Share[] | undefined, recipients: Recipient[] | undefined): PartnerName[] => {
  return (
    shares
      ?.map((share) => {
        return {
          name: recipients?.find((recipient) => recipient.projectId === share.projectId)?.name ?? '',
        };
      })
      .sort((partnerNameA, partnerNameB) => {
        const nameA = partnerNameA.name.toUpperCase();
        const nameB = partnerNameB.name.toUpperCase();

        if (nameA < nameB) {
          return -1;
        }

        if (nameA > nameB) {
          return 1;
        }

        // names must be equal
        return 0;
      }) ?? []
  );
};
